import { useMemo, useState } from "react";

import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import StatPill from "@plannel/components/ui/StatPill";
import { FS } from "@plannel/utils/font-util";

/*
  규칙이 실제로 덮는 고객/제품 목록 — 계층별로 묶어서 보여준다.

  그리드를 쓰지 않는다. 이 목록은 편집하는 표가 아니라 "이 규칙이 저걸 포함하나?"를 확인하는 집합이라,
  표의 장비(컬럼 메뉴·리사이즈·개인화)가 값을 못 한다. 대신 여러 컬럼으로 접어 담아 밀도를 올린다.

  계층으로 묶는 게 핵심이다. 규칙이 중간 노드를 가리키면 서버는 그 아래 **모든 리프 계층**의
  구성원을 돌려주므로(getLeafHrchyCds), 결과는 여러 가지에서 섞여 온다.
  그룹 헤더만 훑으면 "이 규칙이 어느 가지를 몇 개씩 덮는가"가 바로 읽히고,
  계층명이 행마다 반복되지도 않는다(옛 그리드의 Hierarchy Name 컬럼이 그랬다).

  가상 스크롤 라이브러리가 없어서 그룹당 그리는 개수는 CAP 으로 자른다. 헤더의 개수는 자르기 전
  실제 개수라, 잘려도 "몇 개짜리 가지인지"는 정확히 남는다.
*/
const CAP = 200;

/*
  컬럼 폭은 데이터를 따라간다.

  고정 폭(190px)으로 두면 이름이 긴 테넌트에서 코드가 앞자리를 먹고 이름은 서너 글자만 남는다.
  그렇다고 넉넉히 잡아두면 이름이 짧은 테넌트에서 오른쪽이 통째로 빈다.
  → 지금 보이는 이름들의 p90 길이로 최소 컬럼 폭을 정한다. 이름이 짧으면 3~4열, 길면 1열이 되어
    같은 코드가 양쪽 테넌트에서 다 읽힌다.
  최댓값이 아니라 p90 인 이유: 유난히 긴 이름 하나 때문에 전체가 1열로 무너지면 안 된다.
  그 하나는 말줄임 + title 로 처리한다.
*/
const MIN_COL = 190;
const MAX_COL = 460;
const CHAR_PX = 6.6;   // 12.5px 라틴 문자 평균 폭
const CODE_PX = 48;    // 코드 조각 + 사이 간격
const CELL_PAD = 24;   // 셀 좌우 패딩 + 컬럼 간격

// CJK·한글은 라틴 문자의 약 두 배 폭이라 글자 수를 그대로 세면 한국어/일본어에서 폭이 모자란다.
const WIDE_CHAR = /[ᄀ-ᇿ⺀-꓏가-힣豈-﫿︰-﹏＀-｠￠-￦]/;
const displayWidth = (text) => {
  let width = 0;
  for (const ch of String(text ?? "")) {
    width += WIDE_CHAR.test(ch) ? 2 : 1;
  }
  return width;
};

const RuleMemberList = ({ t, label, rows, codeField, nameField, hrchyField }) => {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());

  const keyword = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!keyword) {
      return rows;
    }
    return rows.filter(row =>
      String(row[codeField] ?? "").toLowerCase().includes(keyword)
      || String(row[nameField] ?? "").toLowerCase().includes(keyword)
      || String(row[hrchyField] ?? "").toLowerCase().includes(keyword));
  }, [rows, keyword, codeField, nameField, hrchyField]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const row of filtered) {
      const name = row[hrchyField] || "—";
      if (!map.has(name)) {
        map.set(name, []);
      }
      map.get(name).push(row);
    }
    return Array.from(map, ([name, members]) => ({ name, members }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, hrchyField]);

  const columnMin = useMemo(() => {
    if (!filtered.length) {
      return MIN_COL;
    }
    const widths = filtered.map(row => displayWidth(row[nameField])).sort((a, b) => a - b);
    // (n-1)*0.9 로 잡아야 목록이 짧을 때도 최댓값을 집지 않는다.
    // floor(n*0.9) 는 n=10 에서 인덱스 9(=최댓값)라, 긴 이름 하나가 전체를 1열로 무너뜨린다.
    const p90 = widths[Math.floor((widths.length - 1) * 0.9)];
    return Math.min(MAX_COL, Math.max(MIN_COL, Math.round(p90 * CHAR_PX + CODE_PX + CELL_PAD)));
  }, [filtered, nameField]);

  const toggle = (name) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, flexShrink: 0 }}>
        <Box sx={{ fontSize: FS.SUB, fontWeight: 600, color: "text.secondary" }}>{label}</Box>
        <StatPill label="" value={filtered.length} sx={{ height: 19, px: 0.75, fontSize: FS.CAPTION }} />
        <Box sx={{ flex: 1 }} />
        <Box sx={{
          display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, maxWidth: 160,
          border: 1, borderColor: "divider", borderRadius: 1, px: 0.75, height: 24,
          "&:focus-within": { borderColor: "primary.main" },
        }}>
          <SearchIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
          <InputBase
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t("search")}
            sx={{ fontSize: FS.BODY, "& input": { p: 0 } }}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 1, pb: 1 }}>
        {groups.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center", fontSize: FS.SUB, color: "text.disabled" }}>{t("MSG_NoData")}</Box>
        ) : groups.map(group => {
          // 검색 중에는 접힘을 무시한다 — 접힌 그룹에 걸린 결과가 안 보이면 검색이 거짓말이 된다.
          const open = !!keyword || !collapsed.has(group.name);
          const shown = open ? group.members.slice(0, CAP) : [];
          const hidden = group.members.length - shown.length;
          return (
            <Box key={group.name}>
              <Box
                onClick={() => toggle(group.name)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.5, pt: 0.75, pb: 0.25,
                  cursor: "pointer", color: "text.secondary", fontSize: FS.BODY,
                  "&:hover": { color: "text.primary" },
                }}
              >
                {open
                  ? <KeyboardArrowDownIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                  : <KeyboardArrowRightIcon sx={{ fontSize: 15, flexShrink: 0 }} />}
                <Box title={group.name} sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {group.name}
                </Box>
                <Box sx={{ color: "text.disabled", flexShrink: 0 }}>{group.members.length}</Box>
                <Box sx={{ flex: 1, height: "1px", bgcolor: "divider", ml: 0.5 }} />
              </Box>

              {open && (
                <Box sx={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${columnMin}px, 1fr))`, columnGap: 1.5 }}>
                  {/*
                    MUI Tooltip 대신 네이티브 title 을 쓴다 — 여기서 툴팁은 잘린 이름을 확인하는
                    보조 수단인데, 그룹마다 최대 200행이면 Tooltip 컴포넌트가 수백 개 붙는다.
                  */}
                  {shown.map(row => (
                    <Box
                      key={row[codeField]}
                      title={`${row[codeField]} · ${row[nameField]}`}
                      sx={{
                        display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0,
                        height: 22, px: 0.75, borderRadius: 1,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled", flexShrink: 0, letterSpacing: "0.02em" }}>
                        {row[codeField]}
                      </Box>
                      <Box sx={{ fontSize: FS.SUB, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row[nameField]}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {open && hidden > 0 && (
                <Box sx={{ pl: 0.75, fontSize: FS.BODY, color: "text.disabled" }}>{`+ ${hidden}`}</Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default withTranslation()(RuleMemberList);
